import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getEmployees,
  createEmployee,
} from "../api/apiFunction/employeesServices";
import { getAllPlan } from "../api/apiFunction/planServices";
import { formatDateTime } from "../utils/helperFunction";
import CSVUploader from "../components/pages/org/CSVUploader";
import {
  Plus,
  MessageCircle,
  UserPlus,
  Users as UsersIcon,
  X,
  Crown,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PlanUpgradeModal from "../components/pages/org/PlanUpgradeModal";
import {
  employeeUpdate,
  updateUserStatus,
} from "../api/apiFunction/userServices";
import EditEmployeeModal from "../components/pages/org/EditEmployeeModal";

// Skeleton loader component for table rows
const TableSkeleton = () => (
  <div className="animate-pulse">
    {[...Array(5)].map((_, index) => (
      <div key={index} className="border-b border-bd-50">
        <div className="px-6 py-4 flex items-center space-x-4">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-bg-40 rounded-full"></div>
            <div className="h-4 bg-bg-40 rounded w-32"></div>
          </div>
          <div className="h-4 bg-bg-40 rounded w-48"></div>
          <div className="h-4 bg-bg-40 rounded w-20"></div>
          <div className="h-4 bg-bg-40 rounded w-24"></div>
          <div className="h-4 bg-bg-40 rounded w-16"></div>
          <div className="h-4 bg-bg-40 rounded w-16"></div>
          <div className="h-8 w-8 bg-bg-40 rounded-full"></div>
        </div>
      </div>
    ))}
  </div>
);

const Employees = ({ organizationId }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [activeUploadTab, setActiveUploadTab] = useState("single");
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    role: "Employee",
  });
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [sortBy, setSortBy] = useState("joined-newest");
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);

  const fetchEmployees = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getEmployees(organizationId);
      setEmployees(Array.isArray(response) ? response : []);
      return true;
    } catch (error) {
      toast.error("Failed to fetch employees", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) {
      fetchEmployees();
    }
  }, [organizationId, fetchEmployees]);

  useEffect(() => {
    if (showAddEmployeeModal) {
      setNewEmployee({ name: "", email: "", role: "Employee" });
    }
  }, [showAddEmployeeModal]);

  let filteredEmployees = employees.filter(
    (employee) =>
      (planFilter === "all" ||
        (employee.plan?.name || "free").toLowerCase() === planFilter) &&
      (employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Sort employees
  filteredEmployees = filteredEmployees.slice(); // copy
  if (sortBy === "name-az") {
    filteredEmployees.sort((a, b) => a.name.localeCompare(b.name));
  } else if (sortBy === "name-za") {
    filteredEmployees.sort((a, b) => b.name.localeCompare(a.name));
  } else if (sortBy === "joined-newest") {
    filteredEmployees.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  } else if (sortBy === "joined-oldest") {
    filteredEmployees.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
  }

  // Pagination state and calculations must come after filteredEmployees is defined
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.ceil(filteredEmployees.length / pageSize);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const navigateToChat = () => {
    navigate("/chat");
  };

  const handleBulkImport = async (employees) => {
    try {
      setIsLoading(true);
      let successCount = 0;
      for (const emp of employees) {
        try {
          await createEmployee({
            name: emp.name,
            email: emp.email,
            organizationId,
          });
          successCount++;
        } catch (error) {
          // Continue with next employee even if one fails
        }
      }
      await fetchEmployees();
      if (successCount > 0) {
        toast.success(`Successfully invited ${successCount} employee(s)`);
      }
      if (successCount < employees.length) {
        toast.warning(
          `Failed to add ${employees.length - successCount} employee(s)`
        );
      }
      setShowAddEmployeeModal(false);
    } catch (error) {
      toast.error("Error adding employees. Please try again.", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.email) {
      toast.error("Please fill in all fields");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmployee.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      setIsLoading(true);
      await createEmployee({
        name: newEmployee.name,
        email: newEmployee.email,
        organizationId,
      });
      setNewEmployee({ name: "", email: "" });
      setShowAddEmployeeModal(false);
      await fetchEmployees();
      toast.success("Employee added successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add employee");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPlanModal = async (employee) => {
    setSelectedEmployee(employee);
    setShowPlanModal(true);
    try {
      const res = await getAllPlan();
      if (res?.data) {
        setPlans(res.data);
      } else {
        setPlans([]);
      }
    } catch {
      setPlans([]);
    }
  };

  const handlePlanSelect = async (plan) => {
    if (!selectedEmployee) return;
    try {
      setIsLoading(true);
      await employeeUpdate(selectedEmployee._id, plan.name);
      toast.success(
        `Plan updated to ${plan.name} for ${selectedEmployee.name}`
      );
      setShowPlanModal(false);
      await fetchEmployees();
    } catch (error) {
      toast.error("Failed to update plan");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusToggle = async (employee) => {
    try {
      setIsToggleLoading(true);
      const newStatus = !employee.status;
      await updateUserStatus(employee._id, newStatus);

      // Update the local state directly instead of refetching
      setEmployees((prevEmployees) =>
        prevEmployees.map((emp) =>
          emp._id === employee._id ? { ...emp, status: newStatus } : emp
        )
      );

      toast.success(
        `${employee.name} status updated to ${
          newStatus ? "Active" : "Inactive"
        }`
      );
    } catch (error) {
      toast.error("Failed to update employee status");
    } finally {
      setIsToggleLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 mt-8">
        <div className="flex flex-1 items-center gap-3">
          {/* Sort By Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-bg-50 border border-bd-50 rounded-lg text-sm text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
            style={{ minWidth: "120px" }}
            aria-label="Sort By"
          >
            <option value="joined-newest">Joined (Newest)</option>
            <option value="joined-oldest">Joined (Oldest)</option>
            <option value="name-az">Name (A-Z)</option>
            <option value="name-za">Name (Z-A)</option>
          </select>
          {/* Filter Dropdown */}
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-3 py-2 bg-bg-50 border border-bd-50 rounded-lg text-sm text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
            aria-label="Filter by Plan"
          >
            <option value="all">All Plans</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          {/* Search Input */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search employees by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 pl-10 bg-bg-50 text-fg-50 border border-bd-50 rounded-lg focus:outline-none focus:ring-1 focus:ring-ac-02"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-fg-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={navigateToChat}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-ac-02 text-white dark:text-black rounded-lg hover:bg-ac-01 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MessageCircle size={16} />
            <span>Chat</span>
          </button>
          <button
            onClick={() => setShowAddEmployeeModal(true)}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-bg-40 text-fg-50 rounded-lg hover:bg-bg-30 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} />
            <span>New Employee</span>
          </button>
        </div>
      </div>
      <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="min-w-full">
            <div className="bg-bg-40 px-6 py-3">
              <div className="flex space-x-4">
                <div className="h-4 bg-bg-50 rounded w-16"></div>
                <div className="h-4 bg-bg-50 rounded w-20"></div>
                <div className="h-4 bg-bg-50 rounded w-12"></div>
                <div className="h-4 bg-bg-50 rounded w-24"></div>
                <div className="h-4 bg-bg-50 rounded w-16"></div>
                <div className="h-4 bg-bg-50 rounded w-16"></div>
                <div className="h-4 bg-bg-50 rounded w-20"></div>
              </div>
            </div>
            <TableSkeleton />
          </div>
        ) : (
          <table className="min-w-full divide-y divide-bd-50">
            <thead className="bg-bg-40">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                  Joined On
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-fg-60 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-bg-60 divide-y divide-bd-50">
              {paginatedEmployees.length > 0 ? (
                paginatedEmployees.map((employee) => (
                  <tr key={employee.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-bg-40 flex items-center justify-center">
                          <UserPlus size={20} className="text-fg-60" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-fg-50">
                            {employee.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-fg-50">{employee.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                        {employee.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-60">
                      {formatDateTime(employee.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const planName = (
                          employee.plan?.name || "Free"
                        ).toLowerCase();
                        let badgeClass =
                          "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                        if (planName === "free")
                          badgeClass =
                            "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                        else if (planName === "pro")
                          badgeClass =
                            "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                        else if (planName === "enterprise")
                          badgeClass =
                            "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                        return (
                          <span
                            className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}
                          >
                            {employee.plan?.name || "Free"}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleStatusToggle(employee)}
                        disabled={isToggleLoading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ac-02 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          employee.status
                            ? "bg-blue-500"
                            : "bg-gray-300 hover:bg-gray-400"
                        }`}
                        title={`Click to ${
                          employee.status ? "deactivate" : "activate"
                        } ${employee.name}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            employee.status ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span
                        className={`ml-2 text-xs font-medium ${
                          employee.status ? "text-blue-600" : "text-gray-500"
                        }`}
                      >
                        {employee.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-4">
                        {/* Plan and Edit actions */}
                        <button
                          type="button"
                          disabled={isLoading}
                          className="p-2 rounded-full hover:bg-bg-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Upgrade"
                          onClick={() => handleOpenPlanModal(employee)}
                        >
                          <Crown size={18} className="text-ac-02" />
                        </button>
                        <button
                          type="button"
                          disabled={isLoading}
                          className="p-2 rounded-full hover:bg-bg-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit Employee"
                          onClick={() => {
                            setEditEmployee(employee);
                            setShowEditEmployeeModal(true);
                          }}
                        >
                          <Pencil size={18} className="text-blue-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-4 text-center text-sm text-fg-60"
                  >
                    {searchQuery
                      ? "No matching employees found"
                      : "No employees found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4 pb-6">
          <button
            className="px-3 py-1 rounded bg-bg-40 text-fg-50 disabled:opacity-50"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={`px-3 py-1 rounded ${
                currentPage === i + 1
                  ? "bg-ac-02 text-white dark:text-black"
                  : "bg-bg-40 text-fg-50"
              }`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="px-3 py-1 rounded bg-bg-40 text-fg-50 disabled:opacity-50"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
      {/* Add Employee Modal */}
      {showAddEmployeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Employee(s)</h3>
              <button
                onClick={() => {
                  setShowAddEmployeeModal(false);
                  setActiveUploadTab("single");
                }}
                className="text-fg-60 hover:text-fg-50"
              >
                <X size={20} />
              </button>
            </div>
            <div className="border-b border-bd-50 mb-6">
              <div className="flex space-x-1">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium rounded-t-md flex items-center space-x-2 ${
                    activeUploadTab === "single"
                      ? "bg-bg-50 text-fg-50 border-t border-l border-r border-bd-50"
                      : "text-fg-60 hover:text-fg-50 hover:bg-bg-50/30"
                  }`}
                  onClick={() => setActiveUploadTab("single")}
                >
                  <UserPlus size={16} />
                  <span>Single</span>
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium rounded-t-md flex items-center space-x-2 ${
                    activeUploadTab === "bulk"
                      ? "bg-bg-50 text-fg-50 border-t border-l border-r border-bd-50"
                      : "text-fg-60 hover:text-fg-50 hover:bg-bg-50/30"
                  }`}
                  onClick={() => setActiveUploadTab("bulk")}
                >
                  <UsersIcon size={16} />
                  <span>Bulk Import</span>
                </button>
              </div>
            </div>
            <div className="mt-4">
              {activeUploadTab === "single" ? (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="employeeName"
                      className="block text-sm font-medium text-fg-50 mb-1"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="employeeName"
                      className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                      placeholder="Enter employee's full name"
                      value={newEmployee.name}
                      onChange={(e) =>
                        setNewEmployee({ ...newEmployee, name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="employeeEmail"
                      className="block text-sm font-medium text-fg-50 mb-1"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="employeeEmail"
                      className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                      placeholder="employee@company.com"
                      value={newEmployee.email}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="employeeRole"
                      className="block text-sm font-medium text-fg-50 mb-1"
                    >
                      Role
                    </label>
                    <input
                      type="text"
                      id="employeeRole"
                      className="w-full py-2 px-3 rounded-md bg-bg-50/50 text-fg-50 border border-bd-50 cursor-not-allowed"
                      value={newEmployee.role}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      className="py-2 px-4 border border-bd-50 rounded-md text-fg-50 hover:bg-bg-50 transition-colors"
                      onClick={() => {
                        setShowAddEmployeeModal(false);
                        setActiveUploadTab("single");
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="py-2 px-4 bg-ac-02 hover:bg-ac-01 rounded-md transition-colors shadow-sm text-white flex items-center"
                      onClick={handleAddEmployee}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>Confirm</span>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <CSVUploader
                  onFileProcessed={handleBulkImport}
                  onClose={() => setShowAddEmployeeModal(false)}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {showPlanModal && selectedEmployee && (
        <PlanUpgradeModal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          currentPlanId={selectedEmployee.plan?._id || selectedEmployee.plan}
          currentPlanName={selectedEmployee.plan?.name || "Free"}
          onPlanSelect={handlePlanSelect}
          plans={plans}
          employeeName={selectedEmployee.name}
          isLoading={isLoading}
        />
      )}
      {showEditEmployeeModal && editEmployee && (
        <EditEmployeeModal
          isOpen={showEditEmployeeModal}
          employee={editEmployee}
          onClose={() => setShowEditEmployeeModal(false)}
          onUpdate={fetchEmployees}
        />
      )}
    </div>
  );
};

export default Employees;
